import { EditOutlined, EyeOutlined, SearchOutlined, DeleteOutlined, } from '@ant-design/icons';
import { BsLock, BsUnlock } from 'react-icons/bs';
import { Button, Input,  Table, Tag, message, Modal, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useDebounce from '../../utils/debounce';
import { useFetchOperatorNames } from '../../Hooks/Index';
import { useToggleOperatorBlock, useDeleteOperator } from './Hooks';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore';
import NewOperatorModal from '../../components/Modals/NewOperatorModal';
import ViewOperatorDrawer from './ViewOperatorDrawer';

export default function Operators() {
  const { t } = useTranslation('operator');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [openOperatorModel, setOpenOperatorModel] = useState(false);
  const [currentOperatorId, setCurrentOperatorId] = useState(null);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const [page, setPage] = useState(1);
  const limit = 10;
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [operatorToBlock, setOperatorToBlock] = useState(null);
  const [viewOperatorDrawerOpen, setViewOperatorDrawerOpen] = useState(false);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState(null);

  const { data: operatorData, isLoading } = useFetchOperatorNames({
    page,
    limit,
    search: debouncedSearch,
  });

  const { mutate: toggleOperatorBlock, isPending: isToggling } =
    useToggleOperatorBlock({
      onSuccess: () => {
        queryClient.invalidateQueries(['listOperators']);
      },
      onError: (error) => {
        message.error(
          error?.response?.data?.message ||
            error?.response?.data?.status_message
        );
      },
    });

  const { mutate: deleteOperator, isPending: isDeleting } = useDeleteOperator({
    onSuccess: () => {
      message.success(t('deleteSuccessMessage'));
      queryClient.invalidateQueries(['listOperators']);
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.status_message
      );
    },
  });

  const transformedData =
    operatorData?.paginatedData.map((operator) => ({
      ...operator,
      contactPerson: `${operator.contactPersonFname} ${operator.contactPersonLname}`,
      isBlocked: operator.status === 'BLOCKED',
      created_at: operator.created_at,
    })) || [];

  const toggleBlock = (operator) => {
    setOperatorToBlock(operator);
    setBlockModalOpen(true);
  };

  const confirmToggleBlock = () => {
    const newStatus =
      operatorToBlock.status === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED';
    toggleOperatorBlock(
      { id: operatorToBlock._id, status: newStatus },
      {
        onSuccess: () => {
          message.success(
            newStatus === 'BLOCKED'
              ? t('blockSuccessMessage')
              : t('unblockSuccessMessage')
          );
          queryClient.invalidateQueries(['listOperators']);
        },
        onError: (error) => {
          message.error(
            error?.response?.data?.message ||
              error?.response?.data?.status_message
          );
        },
      }
    );
    setBlockModalOpen(false);
  };

  const getBlockModalTitle = () => {
    return operatorToBlock?.status === 'BLOCKED'
      ? t('unblockModalTitle')
      : t('blockModalTitle');
  };

  const getBlockModalMessage = () => {
    return operatorToBlock?.status === 'BLOCKED'
      ? t('unblockModalMessage')
      : t('blockModalMessage');
  };

  const closeOperatorModel = () => {
    queryClient.invalidateQueries(['listOperators']);
    setCurrentOperatorId(null);
    setOpenOperatorModel(false);
  };

  const onEditOperator = (id) => {
    setCurrentOperatorId(id);
    setOpenOperatorModel(true);
  };

  const openViewOperatorDrawer = (operator) => {
    setSelectedOperatorId(operator?._id);
    setViewOperatorDrawerOpen(true);
  };

  const closeViewOperatorDrawer = () => {
    setSelectedOperatorId(null);
    setViewOperatorDrawerOpen(false);
  };

  const openDeleteModal = (operator) => {
    setOperatorToDelete(operator);
    setDeleteModalOpen(true);
  };

  const confirmDeleteOperator = () => {
    if (operatorToDelete) {
      deleteOperator(operatorToDelete._id);
    }
    setDeleteModalOpen(false);
  };

  const getDeleteModalTitle = () => t('deleteModalTitle');
  const getDeleteModalMessage = () => t('deleteModalMessage');
  const getDeleteSubMessage = () => t('deleteSubMessage');

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      sorter: (a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      render: (_, record) => (
        <div onClick={() => openViewOperatorDrawer(record)} className="capitalize whitespace-nowrap text-center text-primary cursor-pointer hover:text-[#28A745]">{record.name}</div>
      ),
    },
    {
      title: t('owner'),
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      align: 'center',
      render: (_, record) =>
        `${record.contactPersonFname} ${record.contactPersonLname}`,
      responsive: ['md'],
    },
    {
      title: t('email'),
      dataIndex: 'contactPersonEmailAddress',
      key: 'contactPersonEmailAddress',
      align: 'center',
      responsive: ['md'],
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('phoneNumber'),
      dataIndex: 'contactPersonPhoneNumber',
      key: 'phoneNumber',
      align: 'center',
      className: 'whitespace-nowrap w-40',
      responsive: ['md'],
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        const phoneText = `${record.contactPersonPhoneNumber ? "+" + record.countryCode : ''} ${record.contactPersonPhoneNumber ? record.contactPersonPhoneNumber :""}`;
        return (
          <div>
            {phoneText}
          </div>
        );
      },
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'BLOCKED' ? 'red' : 'green'} variant="outlined">{t(status)}</Tag>
      ),
      responsive: ['md'],
    },
    {
      title: t('Locations'),
      dataIndex: 'locationCount',
      key: 'locationCount',
      align: 'center',
      width: 100,
      responsive: ['md'],
    },
    {
      title: t('TotalChargers'),
      dataIndex: 'chargerCount',
      key: 'chargerCount',
      align: 'center',
      width: 120,
      responsive: ['md'],
    },
    {
      title: t('action'),
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex gap-2">
          <Tooltip title={t('viewOperator')}>
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={() => openViewOperatorDrawer(record)}
            />
          </Tooltip>
          <Tooltip title={t('editOperator')}>
            <Button
              type="default"
              disabled={isReadOnlyUser}
              icon={<EditOutlined />}
              onClick={() => onEditOperator(record._id)}
            />
          </Tooltip>
          {record.status === 'BLOCKED' ? (
            <Tooltip title={t('unblock')}>
              <Button
                type="default"
                disabled={isReadOnlyUser}
                icon={<BsUnlock />}
                onClick={() => toggleBlock(record)}
                loading={isToggling}
              />
            </Tooltip>
          ) : (
            <Tooltip title={t('block')}>
              <Button
                type="default"
                icon={<BsLock />}
                onClick={() => toggleBlock(record)}
                loading={isToggling}
              />
            </Tooltip>
          )}
          <Tooltip title={t("deleteText")}>
            <Button
              type="default"
              danger
              disabled={isReadOnlyUser}
              icon={<DeleteOutlined />}
              onClick={() => openDeleteModal(record)}
              loading={isDeleting}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const topContent = useMemo(
    () => (
      <div>
        <div className="flex justify-between gap-4">
          <Input
            allowClear
            placeholder={t('searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (page !== 1) setPage(1);
            }}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            disabled={isReadOnlyUser}
            onClick={() => setOpenOperatorModel(true)}
          >
            {t('addOperator')}
          </Button>
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, page, t]
  );

  return (
    <div className="space-y-4">
      {topContent}
      <div>
        <Table
          columns={columns}
          dataSource={transformedData}
          loading={isLoading}
          size='small'
          pagination={{
            current: page,
            pageSize: limit,
            total: operatorData?.totalCount ?? 0,
            onChange: (newPage) => setPage(newPage),
            showSizeChanger: false,
            position: ['bottomCenter'],
            size: 'default',
          }}
          rowKey="_id"
          scroll={{ x: 'max-content', y: 60 * 8 }}
          variant="outlined"
        />
        
        {openOperatorModel && (
          <NewOperatorModal
            closeOperatorModel={closeOperatorModel}
            currentOperatorId={currentOperatorId}
            isOpen={openOperatorModel}
          />
        )}
        <Modal
          confirmLoading={isToggling}
          title={getBlockModalTitle()}
          open={blockModalOpen}
          onCancel={() => setBlockModalOpen(false)}
          onOk={confirmToggleBlock}
          okButtonProps={{
            loading: isToggling,
          }}
        >
          <p>{getBlockModalMessage()}</p>
        </Modal>
        <Modal
          confirmLoading={isDeleting}
          title={getDeleteModalTitle()}
          open={deleteModalOpen}
          onCancel={() => setDeleteModalOpen(false)}
          onOk={confirmDeleteOperator}
          okButtonProps={{
            loading: isDeleting,
            danger: true,
          }}
        >
          <p>{getDeleteModalMessage()}</p>
          <p className='mt-2 text-orange-500'>{getDeleteSubMessage()}</p>
        </Modal>
       {viewOperatorDrawerOpen &&
        <ViewOperatorDrawer
          isOpen={viewOperatorDrawerOpen}
          operatorId={selectedOperatorId}
          onClose={closeViewOperatorDrawer}
        />}
      </div>
    </div>
  );
}
